import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import { banners, hotComics, latestUpdates, categories, featuredComics } from '@/mocks/comics';

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % banners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '韩国漫画网 - 免费韩漫在线阅读首页',
    description: '韩国漫画网提供海量免费韩漫在线阅读，每日更新最新热门韩漫，包含校园、恋爱、奇幻、悬疑等各类题材，无需注册即可畅享高清漫画阅读体验。',
    url: 'https://www.koreancomic.cn/',
    isPartOf: {
      '@type': 'WebSite',
      name: '韩国漫画网',
      url: 'https://www.koreancomic.cn/',
    },
  };

  return (
    <>
      <SEOHead
        title="韩国漫画网 - 免费韩漫在线看 | 海量韩漫每日更新"
        description="韩国漫画网提供海量免费韩漫在线阅读，每日更新最新热门韩漫，包含校园、恋爱、奇幻、悬疑等各类题材，无需注册即可畅享高清漫画阅读体验。"
        keywords="韩漫,免费韩漫,韩国漫画,漫画大全,在线漫画,韩漫网站,韩漫在线看"
        canonicalUrl="https://www.koreancomic.cn/"
        structuredData={structuredData}
      />

      <Navbar />

      <main>
        <section className="relative w-full h-[420px] md:h-[580px] overflow-hidden">
          {banners.map((banner, index) => (
            <Link
              key={banner.comicId}
              to={`/comic/${banner.comicId}`}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={banner.coverUrl}
                alt={banner.title}
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/40"></div>
              <div className="absolute inset-0 flex items-center">
                <div className="w-full px-4 md:px-6">
                  <div className="max-w-3xl">
                    <span className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-3 py-1 rounded-full mb-3 md:mb-4">
                      {banner.tag}
                    </span>
                    <h2 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-2 md:mb-3">
                      {banner.title}
                    </h2>
                    <p className="text-background-200 text-sm md:text-lg max-w-xl leading-relaxed">
                      {banner.subtitle}
                    </p>
                    <span className="inline-flex items-center gap-2 mt-4 md:mt-6 bg-primary-500 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
                      立即阅读
                      <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              </div>
            </Link>
          ))}

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
            {banners.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentBanner(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentBanner
                    ? 'bg-primary-500 w-6'
                    : 'bg-background-50/50 hover:bg-background-50/80'
                }`}
                aria-label={`切换到第${index + 1}张Banner`}
              />
            ))}
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">本周最受欢迎的韩漫作品</p>
              </div>
              <Link
                to="/category/school"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8 md:mb-10">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                漫画分类
              </h2>
              <p className="text-sm text-foreground-500 mt-1">探索你喜欢的韩漫题材</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group flex items-center gap-4 p-5 bg-background-50 rounded-lg border border-background-200/70 hover:border-primary-200 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-accent-100 rounded-xl group-hover:bg-accent-200 transition-colors flex-shrink-0">
                    <i className={`${cat.icon} text-accent-600 text-xl`}></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-foreground-900">
                      {cat.name}漫画
                    </h3>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {cat.count}部作品
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  最新更新
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚更新的韩漫章节</p>
              </div>
              <Link
                to="/category/fantasy"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
              {latestUpdates.map((comic) => (
                <div key={comic.id} className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 hover:-translate-y-0.5">
                  <Link to={`/comic/${comic.id}`} className="flex items-start gap-3 p-3">
                    <div className="w-16 h-20 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                      <img
                        src={comic.coverUrl}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-sm font-medium text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
                        {comic.title}
                      </h4>
                      <p className="text-xs text-foreground-500 mt-1">
                        {comic.author} · {comic.category}
                      </p>
                      <p className="text-xs text-accent-600 mt-1.5 font-medium">
                        更新至第{comic.chapters.length}话
                      </p>
                      <p className="text-[10px] text-foreground-400 mt-1">
                        {comic.updateDate}
                      </p>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6 md:mb-8">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
                  编辑精选
                </h2>
                <p className="text-sm text-foreground-500 mt-1">品质韩漫，不容错过</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6" data-product-shop="">
              {featuredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} size="large" />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900">
              为什么选择韩国漫画网？
            </h2>
            <p className="text-sm text-foreground-500 mt-2 mb-8 md:mb-10">
              我们致力于为中文读者打造最好的韩漫阅读平台
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-primary-100 rounded-2xl mb-4">
                  <i className="ri-book-read-line text-primary-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                  海量资源
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  收录数千部韩国热门漫画，涵盖校园、恋爱、奇幻、悬疑等各类题材，每日持续更新。
                </p>
              </div>
              <div className="p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 rounded-2xl mb-4">
                  <i className="ri-smartphone-line text-accent-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                  高清画质
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有漫画均为高清分辨率，支持滚动阅读模式，给你流畅的漫画观看体验。
                </p>
              </div>
              <div className="p-6">
                <div className="w-14 h-14 mx-auto flex items-center justify-center bg-secondary-100 rounded-2xl mb-4">
                  <i className="ri-user-smile-line text-secondary-600 text-2xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-2">
                  完全免费
                </h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  无需注册、无需付费，所有漫画均可免费在线观看。只为给你最纯粹的阅读乐趣。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-primary-500">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
              即刻开启韩漫之旅
            </h2>
            <p className="text-background-200 text-sm md:text-base mb-6">
              无需注册，完全免费，海量韩漫等你来发现
            </p>
            <Link
              to="/category/school"
              className="inline-flex items-center gap-2 bg-background-50 text-primary-600 px-6 py-3 rounded-full text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
            >
              开始阅读
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}